/**
 * Enchantment glint for 3D item meshes (vanilla: a scrolling purple
 * `enchanted_glint_item` texture drawn additively over the item a second time).
 * Approximated procedurally: two diagonal bands in view space, scrolling with time.
 * `syncGlint(mesh, on)` keeps a child mesh that shares the parent's geometry.
 */

import * as THREE from 'three';

const time = { value: 0 };
const mats = new Map<THREE.Texture | null, THREE.ShaderMaterial>();
let glintTex: THREE.Texture | null = null;

/** One glint material per item texture: the item's own alpha clips the glint to its pixels
 *  (item meshes are greedy quads whose clear texels are discarded by the item shader). */
function glintMaterial(itemMap: THREE.Texture | null): THREE.ShaderMaterial {
  const have = mats.get(itemMap);
  if (have) return have;
  if (!glintTex) {
    glintTex = new THREE.TextureLoader().load('mc/misc/enchanted_glint_item.png');
    glintTex.wrapS = glintTex.wrapT = THREE.RepeatWrapping;
    glintTex.colorSpace = THREE.SRGBColorSpace;
  }
  const mat = new THREE.ShaderMaterial({
    uniforms: { uTime: time, uGlint: { value: glintTex }, uItem: { value: itemMap }, uHasItem: { value: itemMap ? 1 : 0 } },
    vertexShader: /* glsl */`
      uniform float uTime;
      varying vec2 vUv;
      varying vec2 vItemUv;
      void main() {
        vItemUv = uv;
        // vanilla setupGlintTexturing: translate(-x, y) . rotateZ(10deg) . scale(8) on the item's atlas
        // UVs (a cell is ~1/64 of the atlas, so per-item UVs scale by 8/64 = 1/8)
        vec2 t = uv * 0.125;
        float c = cos(0.17453), s = sin(0.17453);
        t = vec2(c * t.x - s * t.y, s * t.x + c * t.y);
        // glint speed 0.5: x period 27.5 s, y period 7.5 s
        vUv = t + vec2(-uTime / 27.5, uTime / 7.5);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */`
      uniform sampler2D uGlint;
      uniform sampler2D uItem;
      uniform float uHasItem;
      varying vec2 vUv;
      varying vec2 vItemUv;
      void main() {
        if (uHasItem > 0.5 && texture2D(uItem, vItemUv).a < 0.1) discard;
        vec3 g = texture2D(uGlint, vUv).rgb * 0.75; // default glint strength
        gl_FragColor = vec4(g, 1.0);
      }`,
    transparent: true,
    // (SRC_COLOR, ONE): additive, weighted by the glint colour itself
    blending: THREE.CustomBlending,
    blendSrc: THREE.SrcColorFactor,
    blendDst: THREE.OneFactor,
    blendEquation: THREE.AddEquation,
    depthWrite: false,
    depthFunc: THREE.LessEqualDepth,
    side: THREE.DoubleSide,
  });
  mats.set(itemMap, mat);
  return mat;
}

/** Advance the shared glint animation (call once a frame). */
export function tickGlint(dt: number): void { time.value = (time.value + dt) % 1000; }

/** Show/hide a glint overlay on `mesh` (re-uses its current geometry). */
export function syncGlint(mesh: THREE.Mesh, on: boolean): void {
  let g = mesh.userData.glint as THREE.Mesh | undefined;
  if (!on) { if (g) g.visible = false; return; }
  const um = (mesh.material as THREE.ShaderMaterial).uniforms?.uMap?.value as THREE.Texture | undefined;
  const mat = glintMaterial(um ?? null);
  if (!g) {
    g = new THREE.Mesh(mesh.geometry, mat);
    g.frustumCulled = false;
    g.renderOrder = mesh.renderOrder + 1;
    mesh.add(g);
    mesh.userData.glint = g;
  }
  g.geometry = mesh.geometry;
  g.material = mat;
  g.visible = true;
}
